import { Accordion } from '@tvg/fuse/accordion';
import { gridConfig } from '@tvg/fuse/grid';
import { EUserAction } from 'client/config';
import useComponentId from 'client/hooks/useComponentId';
import useIsFacetActive from 'client/hooks/useIsFacetActive';
import useMediaQuery from 'client/hooks/useMediaQuery';
import { usePLPNavigate } from 'client/hooks/usePLPNavigate';
import { useDrawerState } from 'client/state/Drawer';
import { useFacetListState } from 'client/state/FacetList';
import { ACCORDION_TITLE_SIZE } from 'client/utils/consts';
import { FacetType } from 'common/types/facet';
import { PLPGeneralFacetValue, PLPGroupedFacetValue } from 'common/types/plp-data';
import React, { useCallback, useMemo, useRef } from 'react';
import useActiveFacetScroll from '../hooks/useActiveFacetScroll';
import DrawerFacetValues from './DrawerFacetValues';
import { FacetItemInternals } from './FacetemInternals';
import styles from './FacetItem.module.css';

export type FacetItemProps = {
  id: string;
  name: string;
  values: PLPGeneralFacetValue[] | PLPGroupedFacetValue[];
  isDrawerView: boolean;
};

function FacetItem({ id, name, values, isDrawerView = false }: FacetItemProps) {
  const setActiveFacet = useFacetListState((s) => s.setActiveFacet);
  const toggleShowViewProducts = useDrawerState((s) => s.toggleShowViewProducts);
  const plpNavigate = usePLPNavigate();
  const facetRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery(`(min-width: ${gridConfig.l.minWidth})`);
  const componentId = useComponentId(`facet-accordion-${id}`);

  const isFacetActive = useIsFacetActive(id);
  useActiveFacetScroll(isFacetActive, facetRef);

  const isBaseFacetType = useMemo(
    () => id !== FacetType.brand && id !== FacetType.rating && id !== FacetType.colours && id !== FacetType.navcolour,
    [id],
  );

  const handleChange = useCallback(
    (e: React.FormEvent<HTMLElement>) => {
      e.stopPropagation();
      const el = e.target as HTMLInputElement;
      plpNavigate({ facet: { id, option: el.value } }, EUserAction.FACET);
      toggleShowViewProducts.on();
      setActiveFacet(id);
    },
    [setActiveFacet, id, toggleShowViewProducts, plpNavigate],
  );

  const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = (event) => {
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();

      const targetElement = event.target as HTMLElement;
      let checkbox: HTMLInputElement | null = null;

      if (targetElement.tagName === 'A') {
        checkbox = targetElement.querySelector('input[type="checkbox"]') as HTMLInputElement;
      } else {
        checkbox = targetElement.closest('div')?.querySelector('input[type="checkbox"]') as HTMLInputElement;
      }

      if (checkbox) {
        checkbox.focus();
        checkbox.checked = !checkbox.checked;
        const changeEvent = {
          target: checkbox,
          preventDefault: () => {},
          stopPropagation: () => {},
        } as React.ChangeEvent<HTMLInputElement>;

        handleChange(changeEvent);
      }
    }
  };

  const onClickAccordion = useCallback(() => setActiveFacet(id), [id, setActiveFacet]);

  return (
    <>
      {!isDrawerView && (
        <Accordion
          className={styles.facetItem__accordion}
          ref={facetRef}
          heading={name}
          open={isFacetActive}
          onClick={onClickAccordion}
          dataTestId="facet-accordion"
          componentId={componentId}
          titleSize={ACCORDION_TITLE_SIZE}
        >
          <FacetItemInternals
            id={id}
            values={values}
            name={name}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            isBaseFacetType={isBaseFacetType}
          />
        </Accordion>
      )}
      {isDrawerView && !isDesktop && (
        <DrawerFacetValues
          key={`${id}_${name}`}
          id={id}
          name={name}
          values={values}
          isBaseFacetType={isBaseFacetType}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
      )}
    </>
  );
}

export default React.memo(FacetItem);
